import { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { mapValues, noop, omit } from 'lodash-es';
import {
  PARTIAL_SUGGESTED_ROUTE_TYPES,
  SUGGESTED_CURRY_SERVICE_LEG_MESSAGE,
  SUGGESTED_TEDS_LEG_MESSAGE,
} from '@lp/features/RoutePlanner/config';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import {
  fetchGroundLegDistances,
  getAffectedRouteLegs,
  getLegsToRemove,
  getResourcesOnRoute,
  getSuggestedResourcesToApply,
  getSuggestedRouteCleanUpTimeMs,
  getSuggestedRouteData,
  getSuggestedRouteStartTime,
} from '@lp/features/RoutePlanner/utils';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteMainLocations } from '@lp/features/RoutePlanner/hooks/useRouteMainLocations';
import { CaseStatus, DonorType } from '@/api/apis/case';
import {
  AviaLegTypes,
  GroundLegTypes,
  RouteLegTypeEnum,
  RouteLocationType,
  RouteTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { retrieveUnifiedError } from '@/api/utils';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useCaseReviewsDataQuery } from '@/queries/case';
import { useAddPartialSuggestedRoutes, useAddSuggestedRoutes } from '@/queries/route-planner';
import { isAllowedStatus } from '@/utils/helpers';
import { getSegmentMapKey } from '@/utils/route';
import { RoutePlanType } from '@/types/route';
import { useSuggestedMutations } from './useSuggestedMutations';

const { logger } = Sentry;

const RESOURCE_RESERVATION_ERROR = 'RESOURCE_RESERVATION_ERROR';

const getGroundDistances = async (
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: RouteLocationType[],
  routePlanId: string,
): Promise<Map<string, number>> => {
  try {
    return await fetchGroundLegDistances(legs, locations);
  } catch (e) {
    logger.warn('Ground distance calculation failed', {
      error: retrieveUnifiedError(e).message,
      routePlanId,
    });

    return new Map();
  }
};

type ApplyRouteTarget = {
  taskId: string;
  routeIndex: number;
  routeType: RouteTypeSettingsEnum | null;
};

type ApplySuggestedRouteProps = ApplyRouteTarget & {
  routePlan: RoutePlanType;
  changeStatusText?: (status: string) => void;
};

export const useApplySuggestedRoute = () => {
  const { onOpenModal, onCloseModal } = useRouteModalActions();

  const { caseId, donorType, status } = useBaseCaseDetails();
  const caseLocations = useRouteMainLocations();
  const { id, version } = useLogisticsData();
  const {
    id: mainRouteId,
    legs: mainLegs,
    accommodationLegs: mainAccommodationLegs,
  } = useRouteDetails(RouteTypeEnum.Main);
  const {
    id: dryRunRouteId,
    legs: dryRunLegs,
    accommodationLegs: dryRunAccommodationLegs,
  } = useRouteDetails(RouteTypeEnum.DryRun);
  const routeResources = useRouteResources();
  const { handleResourceAssignment } = useResourcesHandlers({ id: caseId });

  const { setGeneratedRoutesStatistics } = useSuggestedMutations();
  const { refetch: refetchCaseReviewsData } = useCaseReviewsDataQuery(caseId);
  const { mutateAsync: addSuggestedRoutes } = useAddSuggestedRoutes();
  const { mutateAsync: addPartialSuggestedRoutes } = useAddPartialSuggestedRoutes();

  const mainRouteLegs = useMemo(() => [...mainLegs, ...mainAccommodationLegs], [mainLegs, mainAccommodationLegs]);
  const dryRunRouteLegs = useMemo(
    () => [...dryRunLegs, ...dryRunAccommodationLegs],
    [dryRunLegs, dryRunAccommodationLegs],
  );

  const [isApplyLoading, setApplyLoading] = useState(false);
  const [applyStatusText, setApplyStatusText] = useState('');
  const [failedApplyResourcesPlanId, setFailedApplyResourcesPlanId] = useState('');
  const [appliedPlanId, setAppliedPlanId] = useState('');

  const applySuggestedRoute = useCallback(
    async ({ routePlan, taskId, routeIndex, routeType, changeStatusText }: ApplySuggestedRouteProps) => {
      setFailedApplyResourcesPlanId('');
      changeStatusText?.('Start applying suggested route.');

      const isPartialRoute = !!routeType && PARTIAL_SUGGESTED_ROUTE_TYPES.includes(routeType);
      const isDryRunReadOnly = isAllowedStatus({ statusFrom: CaseStatus.DeployedToTXCenter, currentStatus: status });

      const resources = getSuggestedResourcesToApply(
        omit(routeResources, 'Crew', 'Aircraft'),
        omit(routePlan.resources, 'Aircraft'),
      );

      if (!isPartialRoute) {
        const removableLegIds = new Set([
          ...getLegsToRemove(mainRouteLegs, status),
          ...getLegsToRemove(dryRunRouteLegs, status, isDryRunReadOnly),
        ]);
        const keptResourceIds = getResourcesOnRoute(mainRouteLegs, dryRunRouteLegs, removableLegIds);
        const removeResources = mapValues(resources.removeResources, (ids) =>
          ids.filter((resourceId) => !keptResourceIds.includes(resourceId)),
        );

        const hasResourcesToRemove = Object.values(removeResources).some((ids) => ids.length);
        if (hasResourcesToRemove) {
          changeStatusText?.('Removing reserved resources.');
          await handleResourceAssignment({
            data: removeResources,
            type: 'remove',
            withRefetch: false,
          });
        }
      }

      const hasResourcesToReserve = Object.keys(resources.reserveResources).length;
      if (hasResourcesToReserve) {
        try {
          changeStatusText?.('Reserving suggested resources.');
          await handleResourceAssignment({
            data: resources.reserveResources,
            type: 'reserve',
            withRefetch: true,
            showToastError: false,
          });
        } catch (e) {
          setFailedApplyResourcesPlanId(routePlan.id);
          throw new Error(RESOURCE_RESERVATION_ERROR);
        }
      }

      changeStatusText?.('Saving suggested route.');

      await setGeneratedRoutesStatistics({
        caseId,
        appliedSuggestedRoutes: JSON.stringify([
          {
            taskId,
            routeIndex,
          },
        ]),
      });

      const withDryRun = !isPartialRoute && donorType === DonorType.DCD;

      changeStatusText?.('Calculating ground distances.');

      const groundDistances = await getGroundDistances(
        [...routePlan.legs.values(), ...(withDryRun ? routePlan.dryRunLegs.values() : [])].flat(),
        [...caseLocations, ...routePlan.locations],
        routePlan.id,
      );

      const mainSuggestedRoute = getSuggestedRouteData({
        routeType: RouteTypeEnum.Main,
        routeId: mainRouteId,
        segments: routePlan.segments,
        legs: routePlan.legs,
        accommodationLegs: routePlan.accommodationLegs,
        locations: routePlan.locations,
        isHubToDhRoute: routeType === RouteTypeSettingsEnum.Hub_DH,
        groundDistances,
      });

      const dryRunSuggestedRoute = withDryRun
        ? getSuggestedRouteData({
            routeType: RouteTypeEnum.DryRun,
            routeId: dryRunRouteId,
            segments: routePlan.dryRunSegments,
            legs: routePlan.dryRunLegs,
            accommodationLegs: routePlan.dryRunAccommodationLegs,
            locations: routePlan.locations,
            groundDistances,
          })
        : null;

      const applyRoutes = isPartialRoute ? addPartialSuggestedRoutes : addSuggestedRoutes;

      await applyRoutes({
        logisticsId: id,
        version,
        suggestedRoutes: [mainSuggestedRoute, ...(dryRunSuggestedRoute ? [dryRunSuggestedRoute] : [])],
      });

      setAppliedPlanId(routePlan.id);

      await refetchCaseReviewsData();
    },
    [
      id,
      version,
      status,
      routeResources,
      mainRouteLegs,
      dryRunRouteLegs,
      handleResourceAssignment,
      setGeneratedRoutesStatistics,
      caseId,
      caseLocations,
      mainRouteId,
      donorType,
      dryRunRouteId,
      addSuggestedRoutes,
      addPartialSuggestedRoutes,
      refetchCaseReviewsData,
    ],
  );

  const confirmedApplyRoute = useCallback(
    async (selectedRoutePlan: RoutePlanType, { taskId, routeIndex, routeType }: ApplyRouteTarget) => {
      try {
        setApplyLoading(true);

        if (routeType !== RouteTypeSettingsEnum.Hub_DH) {
          const txToHubSegment = selectedRoutePlan.segments.find((s) => s.type === SegmentTypeEnum.hub);
          const txToHubLegs = txToHubSegment ? selectedRoutePlan.legs.get(getSegmentMapKey(txToHubSegment)) || [] : [];

          const isTxToHubEmpty = !txToHubLegs.length;
          const isTxToHubWithTeds = txToHubLegs.some((l) => l.legType === RouteLegTypeEnum.TmdxDriveTedsGroundLeg);

          if (isTxToHubEmpty || isTxToHubWithTeds) {
            const message = isTxToHubWithTeds ? SUGGESTED_TEDS_LEG_MESSAGE : SUGGESTED_CURRY_SERVICE_LEG_MESSAGE;

            onOpenModal({
              type: RouteModalTypes.DeviceDeliveryInfo,
              context: { message },
            });
          }
        }

        await applySuggestedRoute({
          routePlan: selectedRoutePlan,
          taskId,
          routeIndex,
          routeType,
          changeStatusText: setApplyStatusText,
        });
      } catch (e) {
        const error = retrieveUnifiedError(e);

        logger.error('Suggested route apply error', {
          error: error.message,
          originalMessage: error.originalMessage,
          routePlanId: selectedRoutePlan.id,
        });

        if (error.originalMessage !== RESOURCE_RESERVATION_ERROR) {
          toast.error(error.message);
        }
      } finally {
        setApplyStatusText('');
        setApplyLoading(false);
      }
    },
    [applySuggestedRoute, onOpenModal],
  );

  const applyRoute = useCallback(
    (selectedRoutePlan: RoutePlanType | null, target: ApplyRouteTarget) => {
      if (!selectedRoutePlan) {
        return;
      }

      const { routeType } = target;
      const isPartialRoute = !!routeType && PARTIAL_SUGGESTED_ROUTE_TYPES.includes(routeType);
      const cleanUpTimeMs = getSuggestedRouteCleanUpTimeMs(selectedRoutePlan.segments, selectedRoutePlan.legs);
      const affectedRouteLegs = getAffectedRouteLegs({
        isPartialRoute,
        mainRouteLegs,
        dryRunRouteLegs,
        cleanUpTimeMs,
      });

      if (!affectedRouteLegs.length) {
        confirmedApplyRoute(selectedRoutePlan, target).catch(noop);

        return;
      }

      onOpenModal({
        type: RouteModalTypes.ReplaceExistingRoute,
        context: {
          isPartialRoute,
          startTime: getSuggestedRouteStartTime(selectedRoutePlan.segments, selectedRoutePlan.legs),
          onConfirm: () => {
            onCloseModal();
            confirmedApplyRoute(selectedRoutePlan, target).catch(noop);
          },
        },
      });
    },
    [confirmedApplyRoute, onOpenModal, onCloseModal, mainRouteLegs, dryRunRouteLegs],
  );

  return { isApplyLoading, applyStatusText, failedApplyResourcesPlanId, appliedPlanId, applyRoute };
};
